import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggle, MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';

import { KanbanProcessoStruct } from '../../../../../shared/services/structs/vendas/kanban-processo.struct';
import { EtapaKanbanStruct } from '../../../../../shared/services/structs/vendas/etapa-kanban.struct';
import { KanbanCor } from '../../../../../shared/services/models/vendas/kanban-cor.model';
import { TipoEtapaKanban } from '../../../../../shared/services/models/vendas/tipo-etapa-kanban.model';
import { TipoEtapaKanbanEnum } from '../../../../../shared/enums/vendas/tipo-etapa-kanban.enum';

export interface KanbanToggleAtivoEvent {
  isAtivo: boolean;
  toggleRef?: MatSlideToggle;
}

// Uma seção renderizada dentro da coluna (Fluxo principal / Etapas especiais / Envio e retirada).
export interface KanbanSecao {
  idTipoEtapaKanban: number;
  titulo: string;
  nomeIcone: string;
  textoApoio: string | null;
  etapas: EtapaKanbanStruct[];
  // Só o fluxo principal permite arrastar e receber novas etapas.
  isFluxoPrincipal: boolean;
}

@Component({
  selector: 'app-kanban-processo-coluna',
  standalone: true,
  imports: [
    CommonModule, FormsModule, DragDropModule, MatIconModule,
    MatMenuModule, MatTooltipModule, MatSlideToggleModule, MatButtonModule,
  ],
  templateUrl: './kanban-processo-coluna.component.html',
  styleUrls: ['./kanban-processo-coluna.component.css'],
})
export class KanbanProcessoColunaComponent {
  @Input({ required: true }) processo!: KanbanProcessoStruct;
  @Input() iconesById: Map<number, string> = new Map();
  @Input() coresById: Map<number, KanbanCor> = new Map();
  @Input() tiposEtapaKanban: TipoEtapaKanban[] = [];
  @Input() tipoIcone = 'category';
  @Input() subtitulo = '';
  // Vem do lookup tipo_processo_kanban — processo obrigatório exibe selo em vez de toggle.
  @Input() isObrigatorio = false;

  @Output() editar = new EventEmitter<EtapaKanbanStruct>();
  @Output() excluir = new EventEmitter<EtapaKanbanStruct>();
  @Output() adicionar = new EventEmitter<void>();
  @Output() reordenar = new EventEmitter<number[]>();
  @Output() toggleAtivo = new EventEmitter<KanbanToggleAtivoEvent>();

  /**
   * Seções da coluna, na ordem do protótipo: Fluxo principal, Envio e retirada e
   * Etapas especiais. Seção sem nenhuma etapa não é renderizada — é o que faz a faixa
   * "Envio e retirada" sumir por completo quando as duas rotinas estão desligadas
   * (o backend simplesmente não envia os marcadores).
   */
  public get secoes(): KanbanSecao[] {
    const ordemDeExibicao = [
      TipoEtapaKanbanEnum.FluxoPrincipal,
      TipoEtapaKanbanEnum.EnvioRetirada,
      TipoEtapaKanbanEnum.Especial,
    ];

    return ordemDeExibicao
      .map(idTipo => {
        const etapas = this.processo.etapas.filter(s => s.idTipoEtapaKanban === idTipo);
        const tipo = this.tiposEtapaKanban.find(t => t.idTipoEtapaKanban === idTipo);
        const isFluxoPrincipal = idTipo === TipoEtapaKanbanEnum.FluxoPrincipal;
        return {
          idTipoEtapaKanban: idTipo,
          titulo: tipo?.descricao ?? '',
          nomeIcone: tipo?.nomeIcone ?? 'label',
          // O fluxo principal tem duas redações: a com "selo FIXO" só vale quando o
          // processo realmente tem alguma etapa fixa (hoje, só o Pedido de Venda).
          textoApoio: isFluxoPrincipal && this.temEtapaFixaNoFluxo
            ? (tipo?.textoApoioComFixo ?? tipo?.textoApoio ?? null)
            : (tipo?.textoApoio ?? null),
          etapas,
          isFluxoPrincipal,
        };
      })
      .filter(secao => secao.etapas.length > 0);
  }

  /** Especiais e marcadores não entram na contagem exibida no cabeçalho. */
  public get qtdEtapaFluxoPrincipal(): number {
    return this.processo.etapas.filter(
      s => s.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal
    ).length;
  }

  private get temEtapaFixaNoFluxo(): boolean {
    return this.processo.etapas.some(
      s => s.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal && s.isFixo
    );
  }

  // Sem fallback: id_kanban_cor é FK NOT NULL, então sempre resolve. Se o lookup não tiver
  // carregado, o binding fica indefinido e o CSS aplica o valor default do próprio var().
  public cor(idKanbanCor: number): KanbanCor | undefined {
    return this.coresById.get(idKanbanCor);
  }

  public iconeNome(idKanbanIcone: number): string {
    return this.iconesById.get(idKanbanIcone) ?? 'label';
  }

  /**
   * Impede que uma etapa comum seja solta numa posição ocupada por uma fixa. Sem isso,
   * arrastar um card para o topo empurraria "Em aberto"/"Aprovado" para baixo — e a posição
   * deles é fixa por dependência das automações.
   * Arrow function: o CDK chama o predicate fora do contexto do componente.
   */
  public podeSoltarNaPosicao = (index: number): boolean => {
    const fluxoPrincipal = this.processo.etapas.filter(
      s => s.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal
    );
    return !fluxoPrincipal[index]?.isFixo;
  };

  public tooltipMotivo(etapa: EtapaKanbanStruct): string {
    return etapa.idsMotivoTransicao.length
      ? `${etapa.idsMotivoTransicao.length} motivo(s) escolhido(s) para esta etapa.`
      : 'Todos os motivos ativos ficam disponíveis.';
  }

  /**
   * O arraste acontece dentro da seção de fluxo principal, então os índices do evento são
   * relativos a ela. Recompõe a lista completa preservando as demais seções e emite a nova
   * ordem — o backend consome a posição de toda etapa recebida, inclusive das fixas.
   */
  public drop(event: CdkDragDrop<EtapaKanbanStruct[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    const fluxoPrincipal = this.processo.etapas.filter(
      s => s.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal
    );
    const demais = this.processo.etapas.filter(
      s => s.idTipoEtapaKanban !== TipoEtapaKanbanEnum.FluxoPrincipal
    );

    moveItemInArray(fluxoPrincipal, event.previousIndex, event.currentIndex);
    this.processo.etapas = [...fluxoPrincipal, ...demais];
    this.reordenar.emit(fluxoPrincipal.map(s => s.idEtapaKanban));
  }

  public onToggle(event: MatSlideToggleChange): void {
    this.toggleAtivo.emit({ isAtivo: event.checked, toggleRef: event.source });
  }

  public ativarProcesso(): void {
    this.toggleAtivo.emit({ isAtivo: true });
  }
}
